export const SUPREME_COURT_CASE_TAXONOMY_CONFIG = {
  id: 'supreme_court',
  label: 'Supreme Court',
  shortLabel: 'Supreme Court',
  description:
    'Civil and criminal petitions for leave, special leave petitions, human rights cases, review petitions, and Supreme Court miscellaneous matters.',
  active: true,
  types: [
    {
      id: 'Civil Petition for Leave to Appeal (CPLA)',
      label: 'Civil Petition for Leave to Appeal (CPLA)',
      shortLabel: 'CPLA',
      description:
        'Civil petition for leave to appeal against judgment, decree, or final order where special leave is sought before the Supreme Court.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Petition', 'Applications', 'Record', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation',
        'Certified Copies / Documents',
        'Petition Drafting',
        'Filing',
        'Office Objections',
        'Listing',
        'Preliminary Hearing',
        'Notice to Respondent',
        "Respondent's Reply",
        'Rejoinder',
        'Leave Granted / Refused',
        'Regular Hearing (if leave granted)',
        'Arguments',
        'Judgment',
        'Review / Further Remedy',
        'Closed',
      ],
    },
    {
      id: 'Criminal Petition for Leave to Appeal',
      label: 'Criminal Petition for Leave to Appeal',
      shortLabel: 'Criminal CPLA',
      description:
        'Criminal petition for leave to appeal against conviction, acquittal, sentence, or final criminal order where special leave is sought.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Petition', 'Applications', 'Record', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation',
        'Certified Copies',
        'Petition Drafting',
        'Filing',
        'Listing',
        'Preliminary Hearing',
        'Notice',
        'Reply',
        'Bail / Suspension of Sentence Application',
        'Leave Granted / Refused',
        'Regular Hearing',
        'Arguments',
        'Judgment',
        'Closed',
      ],
    },
    {
      id: 'Special Leave Petition (SLP)',
      label: 'Special Leave Petition (SLP)',
      shortLabel: 'SLP',
      description:
        'Special leave petition under Article 185(3) of the Constitution against judgment, decree, determination, sentence, or order.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'SLP', 'Applications', 'Record', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation',
        'Certified Copies',
        'SLP Drafting',
        'Filing',
        'Office Objections',
        'Listing',
        'Admission Hearing',
        'Notice',
        'Reply',
        'Leave Granted / Refused',
        'Hearing',
        'Arguments',
        'Judgment',
        'Closed',
      ],
    },
    {
      id: 'Human Rights Case (Article 184(3))',
      label: 'Human Rights Case (Article 184(3))',
      shortLabel: 'Human Rights',
      description:
        'Original jurisdiction proceedings concerning enforcement of fundamental rights of public importance under Article 184(3) of the Constitution.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Petition', 'Applications', 'Record', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation',
        'Petition Drafting',
        'Filing',
        'Listing',
        'Preliminary Hearing',
        'Notice to Respondents',
        'Comments / Reply',
        'Rejoinder',
        'Interim Relief',
        'Further Proceedings',
        'Arguments',
        'Final Order',
        'Compliance',
        'Closed',
      ],
    },
    {
      id: 'Review Petition',
      label: 'Review Petition',
      shortLabel: 'Review',
      description:
        'Review petition against judgment or order of the Supreme Court where review jurisdiction is maintainable.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Review Petition', 'Applications', 'Record', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation',
        'Certified Copy of Impugned Judgment',
        'Review Petition Drafting',
        'Filing',
        'Office Objections',
        'Listing',
        'Preliminary Hearing',
        'Notice',
        'Reply',
        'Arguments',
        'Review Allowed / Dismissed',
        'Closed',
      ],
    },
    {
      id: 'Supreme Court Miscellaneous Application',
      label: 'Supreme Court Miscellaneous Application',
      shortLabel: 'Application',
      description:
        'Connected or standalone applications in Supreme Court proceedings, including stay, early hearing, impleadment, or record production.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Application', 'Main Case Record', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Application Preparation',
        'Filing',
        'Listing',
        'Notice',
        'Reply',
        'Hearing',
        'Order',
        'Compliance',
        'Closed',
      ],
    },
    {
      id: 'Other Supreme Court Matter',
      label: 'Other Supreme Court Matter',
      shortLabel: 'Other',
      description:
        'Use this for Supreme Court matters that do not fit the available Supreme Court case types.',
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Documents', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation',
        'Filing',
        'Listing',
        'Hearing',
        'Order / Judgment',
        'Further Remedy',
        'Closed',
      ],
    },
  ],
};

export const SUPREME_COURT_DEFAULT_FORUM = 'Supreme Court of Pakistan (Islamabad)';

export function resolveSupremeCourtStartingStage(
  matterTypeId: string,
  representingSide?: string,
): string {
  const typeConfig = SUPREME_COURT_CASE_TAXONOMY_CONFIG.types.find((t) => t.id === matterTypeId);
  const stages = typeConfig?.defaultStages ?? ['Case Preparation'];
  const side = representingSide?.toLowerCase() ?? '';
  const isRespondentSide =
    side === 'defendant' || side.includes('respondent') || side.includes('opposing');

  if (isRespondentSide) {
    const replyStage =
      stages.find((s) => /respondent'?s reply/i.test(s)) ??
      stages.find((s) => /^reply$/i.test(s)) ??
      stages.find((s) => /comments \/ reply/i.test(s)) ??
      stages.find((s) => /reply/i.test(s));
    return replyStage ?? stages[Math.min(3, stages.length - 1)];
  }

  return stages[0];
}
